import { useState, useEffect, useRef, useCallback } from 'react';
import type { InternalAgentVisibility, ProgressTree, SessionEvent } from '@localagent/sdk';
import type { CognitiveToolType } from '../../types/reasoning-types';
import { ContextUsageBar } from './ContextUsageBar';
import { ContextEntryTree } from './ContextEntryTree';
import { AgentConfigPanel } from './AgentConfigPanel';
import { formatPersonaName } from '../../utils/persona-display';
import { ExecutionTree } from '@localagent/ui';
import type { ExecutionTreeNode } from '@localagent/ui';
import { InternalAgentIdentity } from './InternalAgentIdentity';
import {
  COGNITIVE_TOOL_LABELS,
  buildChildNodes,
  formatDuration,
  formatTokens,
  stepsToTree,
  type AgentExecutionMetadata,
  type ProgressNodeMetadata,
} from './progress-tree';
import { runIsDefaultOpen } from './live-run';
import {
  extractRunPasses,
  partitionRunByPass,
  type ProcessPassGroup,
} from './process-passes';

// stepsToTree is re-exported so existing importers keep working.
export { stepsToTree } from './progress-tree';


// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: ProgressTree['status'] }) {
  const styles: Record<ProgressTree['status'], string> = {
    completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    stopped: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${styles[status]}`}>
      {status === 'in_progress' && (
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
      )}
      {status.replace('_', ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Detail panel — shows full args + result for a clicked tool node
// ---------------------------------------------------------------------------

/** Older sessions carried agent-shaped metadata under legacy type labels. */
type LegacyAgentMetadata = Omit<AgentExecutionMetadata, 'type'> & {
  type: 'task_execution' | 'graph_node';
};

function NodeDetail({ node, maxLines }: { node: ExecutionTreeNode; maxLines: number }) {
  const meta = node.metadata as ProgressNodeMetadata | LegacyAgentMetadata | undefined;
  if (!meta) return null;

  const truncate = (text: string | undefined) => {
    if (!text) return null;
    const lines = text.split('\n');
    if (lines.length > maxLines) {
      return lines.slice(0, maxLines).join('\n') + '\n...(truncated)';
    }
    return text;
  };

  // LLM call ('thinking') detail — what was thought, what was decided, and the
  // call metrics. Every fragment renders only when its field is present (old
  // events / non-reasoning models show no placeholders).
  if (meta.type === 'llm_call') {
    const metrics: string[] = [];
    if (meta.iteration != null) metrics.push(`iteration ${meta.iteration}`);
    if (meta.duration_ms) metrics.push(formatDuration(meta.duration_ms));
    if (meta.tokens != null && meta.tokens > 0) {
      const split =
        meta.prompt_tokens != null && meta.completion_tokens != null
          ? ` (${formatTokens(meta.prompt_tokens)} in / ${formatTokens(meta.completion_tokens)} out)`
          : '';
      metrics.push(`${formatTokens(meta.tokens)} tokens${split}`);
    }
    if (meta.tokens_per_second != null) metrics.push(`${Math.round(meta.tokens_per_second)} tok/s`);

    return (
      <div className="rounded bg-muted/30 p-2 text-[10px] font-mono space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="font-sans font-medium text-foreground text-xs">{node.label}</span>
          {node.badge && (
            <span className="rounded bg-indigo-100 px-1 text-[10px] text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
              {node.badge}
            </span>
          )}
          {meta.model_name && <span className="text-muted-foreground font-sans">{meta.model_name}</span>}
        </div>
        {metrics.length > 0 && (
          <div className="text-muted-foreground font-sans">{metrics.join(' · ')}</div>
        )}
        {meta.thinking_preview && (
          <div>
            <span className="text-muted-foreground font-sans">Thought:</span>
            <pre className="whitespace-pre-wrap mt-0.5 max-h-64 overflow-y-auto">{truncate(meta.thinking_preview)}</pre>
          </div>
        )}
        {meta.tool_names && meta.tool_names.length > 0 && (
          <div>
            <span className="text-muted-foreground font-sans">Decided to call:</span> {meta.tool_names.join(', ')}
          </div>
        )}
        {meta.response_preview && (
          <div>
            <span className="text-muted-foreground font-sans">Answered:</span>
            <pre className="whitespace-pre-wrap mt-0.5">{truncate(meta.response_preview)}</pre>
          </div>
        )}
        {meta.id && (
          <div className="text-muted-foreground/70 font-sans text-[9px]">id: {meta.id}</div>
        )}
      </div>
    );
  }

  // Context update detail — what the context looked like after this call and
  // what happened to it (composition bar, entry tree, dedupe/compaction).
  if (meta.type === 'context_update') {
    const stats: string[] = [];
    if (meta.message_count != null) stats.push(`${meta.message_count} messages`);
    if (meta.iteration != null) stats.push(`iteration ${meta.iteration}`);
    if (meta.persona_name) stats.push(formatPersonaName(meta.persona_name));

    return (
      <div className="rounded bg-muted/30 p-2 text-[10px] font-mono space-y-1.5">
        <div className="font-sans font-medium text-foreground text-xs">{node.label}</div>
        <ContextUsageBar
          usagePercent={meta.usage_percent ?? null}
          attribution={meta.attribution}
          contextWindowSize={meta.context_window_size}
          compact
        />
        {meta.overflow_handled && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 shrink-0">
              compacted
            </span>
            {meta.messages_summarized != null && meta.messages_summarized > 0 && (
              <span className="text-muted-foreground font-sans">
                {meta.messages_summarized} messages summarized
                {meta.tokens_reduced != null && meta.tokens_reduced > 0 && ` · ${formatTokens(meta.tokens_reduced)} tokens freed`}
              </span>
            )}
          </div>
        )}
        {meta.stale_message_count != null && meta.stale_message_count > 0 && (
          <div className="text-muted-foreground font-sans">
            {meta.stale_message_count} stale entries
            {meta.tokens_saved_by_dedup != null && meta.tokens_saved_by_dedup > 0 && ` · ${formatTokens(meta.tokens_saved_by_dedup)} tokens saved by dedupe`}
          </div>
        )}
        {stats.length > 0 && (
          <div className="text-muted-foreground font-sans">{stats.join(' · ')}</div>
        )}
        {meta.in_context_entries && meta.in_context_entries.length > 0 && (
          <ContextEntryTree
            entries={meta.in_context_entries}
            supersededEntries={meta.superseded_entries ?? []}
            compactedEntries={meta.compacted_entries ?? []}
          />
        )}
        {meta.id && (
          <div className="text-muted-foreground/70 font-sans text-[9px]">id: {meta.id}</div>
        )}
      </div>
    );
  }

  // Agent execution detail (also handles legacy task_execution / graph_node types)
  if (meta.type === 'agent_execution' || meta.type === 'task_execution' || meta.type === 'graph_node') {
    const metrics: string[] = [];
    if (meta.token_count && meta.token_count > 0) metrics.push(`${formatTokens(meta.token_count)} tokens`);
    if (meta.duration_ms) metrics.push(formatDuration(meta.duration_ms));

    return (
      <div className="rounded bg-muted/30 p-2 text-[10px] font-mono space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="font-sans font-medium text-foreground text-xs">{node.label}</span>
          {meta.cognitive_tool_type && (
            <span className="rounded bg-indigo-100 px-1 text-[10px] text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
              {COGNITIVE_TOOL_LABELS[meta.cognitive_tool_type as CognitiveToolType] ?? meta.cognitive_tool_type}
            </span>
          )}
        </div>
        {(meta.id || meta.parent_id) && (
          <div className="text-muted-foreground/70 font-sans text-[9px]">
            {meta.id && <span>id: {meta.id}</span>}
            {meta.parent_id && <span> · parent: {meta.parent_id}</span>}
          </div>
        )}
        {meta.persona_name && (
          <div>
            <span className="text-muted-foreground font-sans">Persona:</span> {formatPersonaName(meta.persona_name)}
          </div>
        )}
        {metrics.length > 0 && (
          <div className="text-muted-foreground font-sans">{metrics.join(' · ')}</div>
        )}
        {meta.context_usage_percent != null && meta.context_usage_percent > 0 && (
          <div className="flex items-center gap-2">
            <ContextUsageBar usagePercent={meta.context_usage_percent} compact />
            {meta.context_has_compaction && (
              <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 shrink-0">
                compacted
              </span>
            )}
          </div>
        )}
        {meta.prompt_preview && (
          <div>
            <span className="text-muted-foreground font-sans">Prompt:</span>
            <pre className="whitespace-pre-wrap mt-0.5">{truncate(meta.prompt_preview)}</pre>
          </div>
        )}
        {meta.conclusion_summary && (
          <div>
            <span className="text-muted-foreground font-sans">Conclusion:</span>
            <pre className="whitespace-pre-wrap mt-0.5">{truncate(meta.conclusion_summary)}</pre>
          </div>
        )}
        {meta.artifacts && meta.artifacts.length > 0 && (
          <div>
            <span className="text-muted-foreground font-sans">Artifacts:</span>
            <ul className="mt-0.5 list-disc list-inside">
              {meta.artifacts.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}
        {meta.decisions && meta.decisions.length > 0 && (
          <div>
            <span className="text-muted-foreground font-sans">Decisions:</span>
            <ul className="mt-0.5 list-disc list-inside">
              {meta.decisions.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </div>
        )}
        {meta.error && (
          <div className="text-red-600 dark:text-red-400">
            <span className="font-sans">Error:</span> {meta.error}
          </div>
        )}
      </div>
    );
  }

  // Tool call detail (default; triage nodes render the same minimal card —
  // their guidance already shows as the row subtitle)
  const tool = meta.type === 'tool_call' ? meta : undefined;
  return (
    <div className="rounded bg-muted/30 p-2 text-[10px] font-mono space-y-1.5">
      <div className="font-sans font-medium text-foreground text-xs">{node.label}</div>
      {tool?.duration_ms && (
        <div className="text-muted-foreground font-sans">{formatDuration(tool.duration_ms)}</div>
      )}
      {(meta.id || tool?.child_id) && (
        <div className="text-muted-foreground/70 font-sans text-[9px]">
          {meta.id && <span>id: {meta.id}</span>}
          {tool?.child_id && <span> · child: {tool.child_id}</span>}
        </div>
      )}
      {tool?.tool_args_preview && (
        <div>
          <span className="text-muted-foreground font-sans">Args:</span>
          <pre className="whitespace-pre-wrap mt-0.5">{tool.tool_args_preview}</pre>
        </div>
      )}
      {tool?.tool_result_preview && (
        <div>
          <span className="text-muted-foreground font-sans">
            Result ({tool.tool_result_status ?? 'unknown'}):
          </span>
          <pre className="whitespace-pre-wrap mt-0.5">{truncate(tool.tool_result_preview)}</pre>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single run card
// ---------------------------------------------------------------------------

/**
 * Evaluation outcome entry for a process pass:
 * met/not-met verdict plus a critique preview.
 */
function PassEvaluationEntry({ pass, evaluation }: {
  pass: number;
  evaluation: NonNullable<ProcessPassGroup['evaluation']>;
}) {
  return (
    <div
      data-testid={`process-evaluation-${pass}`}
      className="rounded bg-muted/30 px-2 py-1.5 text-[10px]"
    >
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-foreground">Evaluation</span>
        <span
          className={`rounded px-1.5 py-0.5 font-medium ${
            evaluation.met
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
          }`}
        >
          {evaluation.met ? 'goal met' : 'goal not met'}
        </span>
      </div>
      {evaluation.critiquePreview && (
        <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{evaluation.critiquePreview}</p>
      )}
    </div>
  );
}

function RunCard({ run, defaultOpen, selected, onSelect, cardRef, showResults, sessionContextChars, events, nextStartedAt, internalAgentVisibility = 'friendly' }: {
  run: ProgressTree;
  defaultOpen: boolean;
  selected: boolean;
  onSelect?: () => void;
  cardRef?: React.Ref<HTMLDetailsElement>;
  showResults: boolean;
  sessionContextChars?: number;
  events?: SessionEvent[];
  /** When the next run has started, an open card must not include its events. */
  nextStartedAt?: string | null;
  internalAgentVisibility?: InternalAgentVisibility;
}) {
  const timeLabel = new Date(run.startedAt).toLocaleTimeString();

  // Agent-process pass grouping: pass boundaries come from
  // process_pass_started events within this run's time window. Legacy /
  // single-pass runs emit none → passes is empty and the run renders exactly
  // as before through the single ungrouped tree.
  const passes = events ? extractRunPasses(events, run, nextStartedAt) : [];
  const treeGroups: Array<{ group: ProcessPassGroup | null; nodes: ExecutionTreeNode[] }> =
    passes.length === 0
      ? [{ group: null, nodes: stepsToTree(run.steps, run.nodes, showResults) }]
      : partitionRunByPass(run, passes).map((partition) => ({
          group: partition.group,
          nodes: buildChildNodes(
            partition.steps,
            partition.rootNodes,
            showResults,
            `p${partition.group.pass}-`,
          ),
        }));
  const treeNodes = treeGroups.flatMap((g) => g.nodes);
  const totalTokens = run.nodes.reduce((sum, t) => sum + (t.tokenCount ?? 0), 0);
  const internalAgents = run.internalAgents ?? run.nodes.flatMap((node) => node.internalAgent ? [node.internalAgent] : []);

  const [expandedNodeId, setExpandedNodeId] = useState<string | undefined>(undefined);
  const [collapsedNodeIds, setCollapsedNodeIds] = useState<Set<string>>(new Set());
  const maxDetailLines = showResults ? 200 : 20;

  const handleToggleCollapse = useCallback((nodeId: string) => {
    setCollapsedNodeIds(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const collectParentNodeIds = useCallback((nodes: ExecutionTreeNode[]): string[] => {
    const ids: string[] = [];
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        ids.push(node.id);
        ids.push(...collectParentNodeIds(node.children));
      }
    }
    return ids;
  }, []);

  const hasCollapsibleNodes = treeNodes.some(n => n.children && n.children.length > 0);

  const handleExpandAll = useCallback(() => {
    setCollapsedNodeIds(new Set());
  }, []);

  const handleCollapseAll = useCallback(() => {
    setCollapsedNodeIds(new Set(collectParentNodeIds(treeNodes)));
  }, [collectParentNodeIds, treeNodes]);

  return (
    <details ref={cardRef} open={defaultOpen} data-testid={`progress-run-${run.runIndex}`} className="rounded">
      <summary
        className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 text-[10px] hover:bg-accent/50 ${selected ? 'bg-primary/5' : ''}`}
        onClick={() => {
          if (onSelect) onSelect();
        }}
      >
        <span className="font-medium text-foreground">Run #{run.runIndex + 1}</span>
        <StatusBadge status={run.status} />
        <span className="text-muted-foreground">
          {run.steps.length} step{run.steps.length !== 1 ? 's' : ''}
          {run.nodes.length > 0 && ` · ${run.nodes.length} agent${run.nodes.length !== 1 ? 's' : ''}`}
          {totalTokens > 0 && ` · ${formatTokens(totalTokens)} tokens`}
        </span>
        <span className="ml-auto text-muted-foreground">{timeLabel}</span>
      </summary>
      <div className="px-2 py-1.5 space-y-1.5">
        {run.error && (
          <p className="text-[10px] text-red-600 dark:text-red-400">{run.error}</p>
        )}

        {run.selectedProcess && (
          <div data-testid="selected-process-details" className="rounded bg-muted/30 px-2 py-1.5 text-[10px]">
            <div className="font-medium text-foreground">Selected process: {run.selectedProcess.name}</div>
            <div className="mt-0.5 text-muted-foreground">{run.selectedProcess.reason}</div>
          </div>
        )}

        {internalAgents.length > 0 && (
          <div className="space-y-1 px-2 py-1.5">
            {internalAgents.map((provenance) => (
              <InternalAgentIdentity
                key={`${provenance.personaName}:${provenance.processId ?? ''}`}
                provenance={provenance}
                visibility={internalAgentVisibility}
              />
            ))}
          </div>
        )}

        {run.agentConfig && (
          <details>
            <summary className="px-2 py-1 text-[10px] font-medium cursor-pointer hover:bg-accent/50 flex items-center gap-1.5 text-muted-foreground">
              Agent Config
              {run.toolNames && (
                <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded">{run.toolNames.length} tools</span>
              )}
            </summary>
            <div className="px-2 py-1.5">
              <AgentConfigPanel config={run.agentConfig} toolNames={run.toolNames} sessionContextChars={sessionContextChars} />
            </div>
          </details>
        )}

        {run.contextUsagePercent > 0 && (
          <ContextUsageBar
            usagePercent={run.contextUsagePercent}
            compact
            agentTokens={run.nodes
              .filter(t => (t.tokenCount ?? 0) > 0)
              .map(t => ({
                personaName: t.personaName ?? '',
                tokenCount: t.tokenCount ?? 0,
                cognitiveToolType: t.cognitiveToolType,
              }))}
          />
        )}

        {treeNodes.length > 0 || passes.length > 0 ? (
          <>
            {hasCollapsibleNodes && (
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleExpandAll} className="text-[10px] text-muted-foreground hover:text-foreground">
                  Expand All
                </button>
                <button type="button" onClick={handleCollapseAll} className="text-[10px] text-muted-foreground hover:text-foreground">
                  Collapse All
                </button>
              </div>
            )}
            {treeGroups.map(({ group, nodes }) =>
              group === null ? (
                <ExecutionTree
                  key="ungrouped"
                  nodes={nodes}
                  onNodeClick={(nodeId) => {
                    setExpandedNodeId(prev => prev === nodeId ? undefined : nodeId);
                  }}
                  expandedNodeId={expandedNodeId}
                  renderNodeDetail={(node) => <NodeDetail node={node} maxLines={maxDetailLines} />}
                  collapsedNodeIds={collapsedNodeIds}
                  onToggleCollapse={handleToggleCollapse}
                  className="text-[11px]"
                />
              ) : (
                <div
                  key={`pass-${group.pass}`}
                  data-testid={`process-pass-${group.pass}`}
                  className="space-y-1.5 p-1.5"
                >
                  <div className="flex items-center gap-1.5 text-[10px]">
                    <span className="font-medium text-foreground">Pass {group.pass}</span>
                    {group.refined && (
                      <span
                        data-testid={`process-refinement-${group.pass}`}
                        className="rounded bg-cyan-100 px-1.5 py-0.5 font-medium text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200"
                        title="This pass re-ran the process with a refined prompt"
                      >
                        refined
                      </span>
                    )}
                  </div>
                  {nodes.length > 0 && (
                    <ExecutionTree
                      nodes={nodes}
                      onNodeClick={(nodeId) => {
                        setExpandedNodeId(prev => prev === nodeId ? undefined : nodeId);
                      }}
                      expandedNodeId={expandedNodeId}
                      renderNodeDetail={(node) => <NodeDetail node={node} maxLines={maxDetailLines} />}
                      collapsedNodeIds={collapsedNodeIds}
                      onToggleCollapse={handleToggleCollapse}
                      className="text-[11px]"
                    />
                  )}
                  {group.evaluation && (
                    <PassEvaluationEntry pass={group.pass} evaluation={group.evaluation} />
                  )}
                </div>
              ),
            )}
          </>
        ) : (
          <p className="text-[10px] text-muted-foreground">No steps recorded</p>
        )}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// ReasoningRunsSection — progress view for the side panel
// ---------------------------------------------------------------------------

export interface ReasoningRunsSectionProps {
  runs: ProgressTree[];
  selectedRunIndex?: number;
  onSelectRun?: (runIndex: number) => void;
  showResults?: boolean;
  /** Approximate character size of the assembled session context / message window,
   *  surfaced (as a token estimate) in each run's AgentConfigPanel. */
  sessionContextChars?: number;
  /** Raw session events — used to derive agent-process pass grouping. */
  events?: SessionEvent[];
  internalAgentVisibility?: InternalAgentVisibility;
  /** A follow-up send before this turn's run exists. History stays closed. */
  pendingTurn?: boolean;
}

export function ReasoningRunsSection({ runs, selectedRunIndex, onSelectRun, showResults = false, sessionContextChars, events, internalAgentVisibility = 'friendly', pendingTurn = false }: ReasoningRunsSectionProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(runs.length);
  const runRefsMap = useRef(new Map<number, HTMLDetailsElement>());

  // Keep the newest run or the pending-turn line in view.
  useEffect(() => {
    if (pendingTurn || runs.length > prevCountRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevCountRef.current = runs.length;
  }, [runs.length, pendingTurn]);

  // Scroll to selected run when selection changes (e.g. from clicking a chat message)
  useEffect(() => {
    if (selectedRunIndex === undefined) return;
    const el = runRefsMap.current.get(selectedRunIndex);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedRunIndex]);

  if (runs.length === 0 && !pendingTurn) {
    return (
      <div className="flex h-full items-center justify-center" data-testid="progress-empty">
        <p className="text-xs text-muted-foreground">No agents yet. Send a message to start.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="progress-runs">
      {runs.map((run, index) => (
        <RunCard
          key={run.runIndex}
          run={run}
          defaultOpen={runIsDefaultOpen(run, runs, pendingTurn)}
          selected={selectedRunIndex === run.runIndex}
          onSelect={() => onSelectRun?.(run.runIndex)}
          showResults={showResults}
          sessionContextChars={sessionContextChars}
          events={events}
          nextStartedAt={runs[index + 1]?.startedAt}
          internalAgentVisibility={internalAgentVisibility}
          cardRef={(el: HTMLDetailsElement | null) => {
            if (el) runRefsMap.current.set(run.runIndex, el);
            else runRefsMap.current.delete(run.runIndex);
          }}
        />
      ))}
      {pendingTurn ? (
        <p className="px-2 py-1.5 text-[11px] text-muted-foreground" data-testid="progress-turn-starting">
          Starting this turn…
        </p>
      ) : null}
      <div ref={bottomRef} />
    </div>
  );
}
